import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik, Form } from 'formik';
import { orderBy } from 'lodash';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import EditRegistrationField from './editRegistrationField';
import { AddButton, IconButton } from '../../components/buttons-visuals';
import ConfirmModal from '../../components/confirmModal';
import { ContentContainer } from '../../components/containers';
import DraggableList from '../../components/draggableList';
import errorLoadingWrapperHOC from '../../components/errorLoadingWrapper/errorLoadingWrapperHOC';
import CustomRegistrationFormField from '../../components/formField/customRegistrationFormField';
import { withLoadDataDefaultConfig } from '../../components/loadData';
import Modal from '../../components/modal';
import { LeagueService } from '../../services/leagueService';
import { AppState } from '../../store';
import { getUserPermissionService } from '../../store/permissions/selector';

interface RegistrationFieldsProps {
    customRegistrationFields: Core.Models.CustomRegistrationField[];
    customRegistrationFieldEntityType: Core.Models.CustomRegistrationFieldEntityType;
    customRegistrationFieldsEnabled: boolean;
    isAdministrator: boolean;
    reloadData: () => Promise<void>;
    title: string;
    uniqueKey: string;
}

const RegistrationFields: React.FunctionComponent<RegistrationFieldsProps> = (props) => {
    const {
        customRegistrationFieldEntityType,
        customRegistrationFields,
        customRegistrationFieldsEnabled,
        isAdministrator,
        reloadData,
        title,
        uniqueKey,
    } = props;

    const [fields, setFields] = useState<Core.Models.CustomRegistrationField[]>(() =>
        orderBy(customRegistrationFields || [], (field) => field.sortOrder ?? 0)
    );
    const [fieldToEdit, setFieldToEdit] = useState<Core.Models.CustomRegistrationField | undefined>(undefined);
    const [fieldToDelete, setFieldToDelete] = useState<Core.Models.CustomRegistrationField | undefined>(undefined);
    const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);

    useEffect(() => {
        setFields(orderBy(customRegistrationFields || [], (field) => field.sortOrder ?? 0));
    }, [customRegistrationFields]);

    const deleteField = async () => {
        if (!fieldToDelete) return;
        await LeagueService.deleteLeagueCustomRegistrationField(fieldToDelete.id);
        setFieldToDelete(undefined);
        await reloadData();
    };

    const updateOrder = useCallback(
        async (nextFields: Core.Models.CustomRegistrationField[]) => {
            setFields(nextFields);
            setIsUpdatingOrder(true);
            try {
                await LeagueService.updateLeagueCustomRegistrationFieldOrder({
                    entityType: customRegistrationFieldEntityType,
                    fieldIds: nextFields.map((field) => field.id),
                });
                await reloadData();
            } catch {
                toast.error('Could not save the new field order. Please try again.');
                setFields(orderBy(customRegistrationFields || [], (field) => field.sortOrder ?? 0));
            } finally {
                setIsUpdatingOrder(false);
            }
        },
        [customRegistrationFieldEntityType, customRegistrationFields, reloadData]
    );

    return (
        <ContentContainer className="global-container-centered" shade={Core.Models.Shades.Dark40}>
            <h3 className="heading-3">{title}</h3>

            {!customRegistrationFieldsEnabled && !isAdministrator && (
                <div>Your league does not support custom registration fields.</div>
            )}

            {(customRegistrationFieldsEnabled || isAdministrator) && (
                <>
                    <AddButton
                        as="button"
                        buttonStyle="solid"
                        buttonSize="medium"
                        className="mb3x"
                        onClick={() =>
                            setFieldToEdit(
                                new Core.Models.CustomRegistrationField({
                                    entityType: props.customRegistrationFieldEntityType,
                                })
                            )
                        }
                    >
                        Add
                    </AddButton>
                    {fields.length > 0 && (
                        <Formik
                            initialValues={{}}
                            key={fields.map((field) => field.id).join('|')}
                            onSubmit={() => {}}
                            render={() => (
                                <Form className="form">
                                    <DraggableList
                                        disabled={isUpdatingOrder}
                                        getId={(field: Core.Models.CustomRegistrationField) => field.id}
                                        handleOnly
                                        items={fields}
                                        renderItem={(
                                            field: Core.Models.CustomRegistrationField,
                                            ix: number,
                                            dragHandleProps?: DraggableProvidedDragHandleProps | null
                                        ) => (
                                            <div className="disp-flex align-center position-relative">
                                                <div
                                                    {...(dragHandleProps ?? {})}
                                                    aria-label={`Reorder ${field.name}`}
                                                    className="league-settings-draggable__handle"
                                                />
                                                <div className="flex-grow ml2x">
                                                    <CustomRegistrationFormField
                                                        fieldArrayName={`customRegistrationFields${uniqueKey}`}
                                                        fieldSubmissionResponse={
                                                            new Core.Models.CustomRegistrationFieldSubmissionResponse(
                                                                field,
                                                                ''
                                                            )
                                                        }
                                                        index={ix}
                                                    />
                                                </div>
                                                <div className="disp-flex align-center">
                                                    <IconButton
                                                        as="button"
                                                        buttonLabel="Edit custom field"
                                                        buttonSize="medium"
                                                        className="mb2x mr ml2x"
                                                        onClick={() => setFieldToEdit(field)}
                                                    >
                                                        <FontAwesomeIcon icon={['fas', 'edit']} />
                                                    </IconButton>
                                                    <IconButton
                                                        as="button"
                                                        buttonLabel="Delete custom field"
                                                        buttonSize="medium"
                                                        className="mb2x"
                                                        onClick={() => {
                                                            setFieldToDelete(field);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={['fas', 'trash-can']} />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        )}
                                        setItems={updateOrder}
                                    />
                                </Form>
                            )}
                        />
                    )}

                    {fields.length <= 0 && <p>No custom registration fields found for this league.</p>}

                    {fieldToEdit && (
                        <Modal onClose={() => setFieldToEdit(undefined)} title="Edit registration field">
                            <EditRegistrationField customRegistrationField={fieldToEdit} reloadData={reloadData} />
                        </Modal>
                    )}

                    {fieldToDelete && (
                        <ConfirmModal
                            confirmText="Yes"
                            onCancel={() => setFieldToDelete(undefined)}
                            onConfirm={deleteField}
                            title="Are you sure"
                        >
                            <p>
                                Are you sure you want to delete <em>{fieldToDelete.name}</em>?
                            </p>
                        </ConfirmModal>
                    )}
                </>
            )}
        </ContentContainer>
    );
};

export default connect((state: AppState) => {
    const league = state.leagueState.league!.league;
    const userPermissionService = getUserPermissionService(state);

    return {
        isAdministrator: userPermissionService.getIsAdmin(),
        customRegistrationFieldsEnabled: typeof league !== 'undefined' && league.customRegistrationFieldsEnabled,
    };
}, {})(
    withLoadDataDefaultConfig(
        errorLoadingWrapperHOC<RegistrationFieldsProps>(RegistrationFields, { loadingOptions: { blockItem: true } }),
        (props: RegistrationFieldsProps) => props,
        async (props: RegistrationFieldsProps) => {
            const userCustomRegistrationFields = await LeagueService.getLeagueCustomRegistrationFields(
                Core.Models.CustomRegistrationFieldEntityType.User
            );

            return {
                ...props,
                customRegistrationFields: userCustomRegistrationFields,
                customRegistrationFieldEntityType: Core.Models.CustomRegistrationFieldEntityType.User,
                title: 'Custom user registration fields',
                uniqueKey: Core.Models.CustomRegistrationFieldEntityType.User.toString(),
            };
        }
    )
);
